import { useState, type ReactNode } from 'react'
import { Box, ButtonBase, CircularProgress, Collapse, Skeleton, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useTranslation } from 'react-i18next'
import { useLocale } from '@/contexts/LocaleContext'
import { LANGUAGE_INTL_LOCALE } from '@/i18n/types'
import { formatDate } from '@/utils/formatDateTime'
import {
  billingService,
  type BillingAccount,
  type SubscriptionState,
} from '@/services/billingService'

/** Stripe's lowercase brand id → how it is printed on the card. */
const CARD_BRANDS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  jcb: 'JCB',
  unionpay: 'UnionPay',
  diners: 'Diners Club',
}

/**
 * The money side of the account, as a settings list: next charge, card,
 * invoices, and the subscription itself.
 *
 * Everything here is read live from Stripe (`GET /api/billing/account/`) and
 * arrives after the counter above it. While it is on the way the rows keep
 * their place with a skeleton instead of jumping in, and if Stripe cannot be
 * reached the rows say so in one line rather than taking the page down.
 *
 * The card and the next charge open the Stripe portal — the one place a card
 * can be changed. Invoices unfold in place: a list of PDFs is something to
 * glance at, not a destination.
 */
export default function BillingDetails({
  account,
  failed,
  subscription,
  onSubscriptionChange,
}: {
  /** undefined while loading. */
  account: BillingAccount | undefined
  failed: boolean
  subscription: SubscriptionState
  onSubscriptionChange: (next: SubscriptionState | null) => void
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('plans')
  const { language } = useLocale()
  const [invoicesOpen, setInvoicesOpen] = useState(false)
  const [portalBusy, setPortalBusy] = useState(false)
  const [subscriptionBusy, setSubscriptionBusy] = useState(false)
  const [actionFailed, setActionFailed] = useState<'portal' | 'subscription' | null>(null)

  const money = (amount: number, currency: string) =>
    new Intl.NumberFormat(LANGUAGE_INTL_LOCALE[language], {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100)

  const openPortal = async () => {
    setPortalBusy(true)
    setActionFailed(null)
    try {
      const url = await billingService.openBillingPortal()
      // Leaving the app entirely — no state set after this matters.
      window.location.href = url
    } catch {
      setActionFailed('portal')
      setPortalBusy(false)
    }
  }

  const cancelled = subscription.cancel_at_period_end
  const toggleSubscription = async () => {
    setSubscriptionBusy(true)
    setActionFailed(null)
    try {
      onSubscriptionChange(
        cancelled
          ? await billingService.resumeSubscription()
          : await billingService.cancelSubscription(),
      )
    } catch {
      setActionFailed('subscription')
    } finally {
      setSubscriptionBusy(false)
    }
  }

  const loading = account === undefined && !failed
  const card = account?.payment_method ?? null
  const upcoming = account?.upcoming ?? null
  const invoices = account?.invoices ?? []
  const accessDate = subscription.ends_at ? formatDate(subscription.ends_at, language) : null

  const nextChargeValue = cancelled
    ? t('billing.noNextCharge')
    : upcoming
      ? upcoming.date
        ? t('billing.nextChargeValue', {
            date: formatDate(upcoming.date, language),
            amount: money(upcoming.amount, upcoming.currency),
          })
        : money(upcoming.amount, upcoming.currency)
      : subscription.next_charge_at
        ? formatDate(subscription.next_charge_at, language)
        : '—'

  const cardValue = card
    ? t('billing.cardValue', {
        brand: CARD_BRANDS[card.brand] ?? card.brand,
        last4: card.last4,
      })
    : t('billing.noCard')

  return (
    <Box>
      <Box
        sx={{
          bgcolor: c.white,
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(27,28,56,.04), 0 8px 24px rgba(27,28,56,.05)',
        }}
      >
        <Row first label={t('billing.nextCharge')} value={nextChargeValue} loading={loading} onClick={openPortal} busy={portalBusy} />
        <Row label={t('billing.paymentMethod')} value={cardValue} loading={loading} onClick={openPortal} busy={portalBusy} />
        <Row
          label={t('billing.invoices')}
          value={invoices.length ? String(invoices.length) : t('billing.noInvoices')}
          loading={loading}
          onClick={invoices.length ? () => setInvoicesOpen((open) => !open) : undefined}
          open={invoicesOpen}
        />
        <Collapse in={invoicesOpen} unmountOnExit>
          <Box sx={{ bgcolor: c.gray50, borderTop: `1px solid ${c.ink08}` }}>
            {invoices.map((invoice) => (
              <Box
                key={invoice.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr auto', sm: '140px 1fr auto auto' },
                  gap: { xs: 0.5, sm: 2 },
                  alignItems: 'center',
                  px: { xs: 2.25, md: 3 },
                  py: 1.5,
                  '& + &': { borderTop: `1px solid ${c.ink08}` },
                }}
              >
                <Typography sx={{ fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>
                  {invoice.date ? formatDate(invoice.date, language) : '—'}
                </Typography>
                <Typography sx={{ fontSize: 14, color: c.ink56, display: { xs: 'none', sm: 'block' } }}>
                  {invoice.number}
                  {invoice.status === 'open' && (
                    <Box component="span" sx={{ ml: 1, color: c.coral, fontWeight: 700 }}>
                      {t('billing.invoiceOpen')}
                    </Box>
                  )}
                </Typography>
                <Typography sx={{ fontSize: 14, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                  {money(invoice.amount, invoice.currency)}
                </Typography>
                {(invoice.pdf_url || invoice.url) && (
                  <Box
                    component="a"
                    href={invoice.pdf_url || invoice.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      gridColumn: { xs: '1 / -1', sm: 'auto' },
                      fontSize: 14,
                      fontWeight: 700,
                      color: c.coral,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {t('billing.invoicePdf')}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Collapse>
        <Row
          label={t('billing.subscription')}
          value={
            cancelled
              ? accessDate
                ? t('subscription.cancelledTitle', { date: accessDate })
                : t('subscription.cancelledTitleNoDate')
              : t('billing.subscriptionActive')
          }
          action={
            <ButtonBase
              onClick={() => void toggleSubscription()}
              disabled={subscriptionBusy}
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: cancelled ? c.coral : c.ink56,
                px: 1.25,
                py: 0.75,
                borderRadius: '10px',
                '&:hover': { bgcolor: alpha(c.coral, 0.06), color: c.coral },
              }}
            >
              {subscriptionBusy ? (
                <CircularProgress size={16} sx={{ color: 'inherit' }} />
              ) : cancelled ? (
                t('subscription.resume')
              ) : (
                t('subscription.cancel')
              )}
            </ButtonBase>
          }
        />
      </Box>

      <Typography sx={{ mt: 1.25, px: 1, fontSize: 13, color: actionFailed || failed ? c.coral : c.ink56 }}>
        {actionFailed === 'portal'
          ? t('subscription.portalFailed')
          : actionFailed === 'subscription'
            ? t('subscription.failed')
            : failed
              ? t('billing.loadFailed')
              : cancelled
                ? t('subscription.cancelledBody')
                : t('subscription.activeBody')}
      </Typography>
    </Box>
  )
}

function Row({
  label,
  value,
  loading = false,
  onClick,
  busy = false,
  open,
  action,
  first = false,
}: {
  label: string
  value: string
  loading?: boolean
  onClick?: () => void
  busy?: boolean
  /** Set on a row that unfolds, so the chevron can turn. */
  open?: boolean
  action?: ReactNode
  first?: boolean
}) {
  const theme = useTheme()
  const c = theme.affexy.colors

  const body = (
    <>
      <Typography sx={{ flex: 1, textAlign: 'left', fontSize: 15 }}>{label}</Typography>
      {loading ? (
        <Skeleton variant="text" width={140} sx={{ fontSize: 15 }} />
      ) : (
        <Typography
          sx={{
            fontSize: 15,
            color: c.ink56,
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
            // On a phone a row with its own button has no room for both; the
            // button says what matters and the line under the list says the rest.
            display: { xs: action ? 'none' : 'block', sm: 'block' },
          }}
        >
          {value}
        </Typography>
      )}
      {action}
      {onClick &&
        (busy ? (
          <CircularProgress size={16} sx={{ color: c.ink32 }} />
        ) : (
          <ChevronRightIcon
            sx={{
              fontSize: 20,
              color: c.ink32,
              transform: open ? 'rotate(90deg)' : 'none',
              transition: 'transform 200ms cubic-bezier(.2,.8,.2,1)',
            }}
          />
        ))}
    </>
  )

  const rowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    width: '100%',
    minHeight: 64,
    px: { xs: 2.25, md: 3 },
    py: 1.5,
    borderTop: first ? 'none' : `1px solid ${c.ink08}`,
  } as const

  if (!onClick || loading) return <Box sx={rowSx}>{body}</Box>
  return (
    <ButtonBase
      onClick={onClick}
      disabled={busy}
      aria-expanded={open}
      sx={{ ...rowSx, '&:hover': { bgcolor: c.gray50 }, '&:active': { bgcolor: c.gray100 } }}
    >
      {body}
    </ButtonBase>
  )
}
